import useFetchItem from '../useFetchItem.js';
import HttpService from '../httpServices';
import { routes } from '../apiroutes';
import { ErrorHandler } from '../errorHandler';

const httpService = new HttpService();

/**
 * AI Analytics Service
 * Handles all AI-powered analytics and predictive insights
 */

// Risk Assessment Hook
export const useRiskAssessment = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['riskAssessment', options.days, options.branch_id],
    queryFn: (params) => {
      const urlParams = new URLSearchParams();
      urlParams.append('days', options.days || 30);
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.append('branch_id', options.branch_id);
      }
      const url = routes.getRiskAssessment(urlParams.toString());
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    riskData: data?.data || [],
    riskError: ErrorHandler(error),
    riskIsLoading: isLoading,
    refetchRisk: refetch,
  };
};

// Attendance Predictions Hook
export const useAttendancePredictions = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['attendancePredictions', options.daysAhead, options.branch_id],
    queryFn: (params) => {
      const urlParams = new URLSearchParams();
      urlParams.append('days_ahead', options.daysAhead || 7);
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.append('branch_id', options.branch_id);
      }
      const url = routes.getAttendancePredictions(urlParams.toString());
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    predictionsData: data?.data || [],
    predictionsError: ErrorHandler(error),
    predictionsIsLoading: isLoading,
    refetchPredictions: refetch,
  };
};

// Attendance Patterns Hook
export const useAttendancePatterns = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['attendancePatterns', options.branch_id],
    queryFn: (params) => {
      const urlParams = {};
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.branch_id = options.branch_id;
      }
      const url = routes.getAttendancePatterns(urlParams);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    patternsData: data?.data || [],
    patternsError: ErrorHandler(error),
    patternsIsLoading: isLoading,
    refetchPatterns: refetch,
  };
};

// Analytics Summary Hook
export const useAnalyticsSummary = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['analyticsSummary', options.branch_id],
    queryFn: (params) => {
      const urlParams = {};
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.branch_id = options.branch_id;
      }
      const url = routes.getAnalyticsSummary(urlParams);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    summaryData: data?.data || {},
    summaryError: ErrorHandler(error),
    summaryIsLoading: isLoading,
    refetchSummary: refetch,
  };
};

// Turnover Predictions Hook
export const useTurnoverPredictions = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['turnoverPredictions', options.branch_id],
    queryFn: (params) => {
      const urlParams = {};
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.branch_id = options.branch_id;
      }
      const url = routes.getTurnoverPredictions(urlParams);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    refetchOnWindowFocus: false
  });

  return {
    turnoverData: data?.data || [],
    turnoverError: ErrorHandler(error),
    turnoverIsLoading: isLoading,
    refetchTurnover: refetch,
  };
};

// Turnover Summary Hook
export const useTurnoverSummary = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['turnoverSummary', options.branch_id],
    queryFn: (params) => {
      const urlParams = {};
      if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
        urlParams.branch_id = options.branch_id;
      }
      const url = routes.getTurnoverSummary(urlParams);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when branch changes
    refetchOnWindowFocus: false
  });

  return {
    turnoverSummaryData: data?.data || {},
    turnoverSummaryError: ErrorHandler(error),
    turnoverSummaryIsLoading: isLoading,
    refetchTurnoverSummary: refetch,
  };
};

// Employee Turnover Prediction Hook
export const useEmployeeTurnoverPrediction = (employeeId, options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['employeeTurnover', employeeId],
    queryFn: (params) => {
      const url = routes.getEmployeeTurnoverPrediction(employeeId);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false && !!employeeId,
    retry: 2
  });

  return {
    employeeTurnoverData: data?.data || {},
    employeeTurnoverError: ErrorHandler(error),
    employeeTurnoverIsLoading: isLoading,
    refetchEmployeeTurnover: refetch,
  };
};

// Department Turnover Insights Hook
export const useDepartmentTurnoverInsights = (departmentId, options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['departmentTurnover', departmentId],
    queryFn: (params) => {
      const url = routes.getDepartmentTurnoverInsights(departmentId);
      return httpService.getData(url);
    },
    enabled: options.enabled !== false && !!departmentId,
    retry: 2
  });

  return {
    departmentTurnoverData: data?.data || {},
    departmentTurnoverError: ErrorHandler(error),
    departmentTurnoverIsLoading: isLoading,
    refetchDepartmentTurnover: refetch,
  };
};

// Combined AI Analytics Hook - OPTIMIZED VERSION
export const useAIAnalytics = (options = {}) => {
  // Step 1: Load summary first (fastest, shows overview)
  const analyticsSummary = useAnalyticsSummary({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false
  });

  // Step 2: Load risk assessment independently (parallel loading)
  const riskAssessment = useRiskAssessment({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Step 3: Load predictions independently (parallel loading)
  const attendancePredictions = useAttendancePredictions({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Step 4: Load patterns independently (parallel loading)
  const attendancePatterns = useAttendancePatterns({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Step 5: Load turnover summary independently (parallel loading)
  const turnoverSummary = useTurnoverSummary({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Step 6: Load turnover predictions independently (parallel loading)
  const turnoverPredictions = useTurnoverPredictions({ 
    enabled: options.enabled,
    branch_id: options.branch_id,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    // Risk Assessment
    riskData: riskAssessment.riskData,
    riskError: riskAssessment.riskError,
    riskIsLoading: riskAssessment.riskIsLoading,
    refetchRisk: riskAssessment.refetchRisk,

    // Predictions
    predictionsData: attendancePredictions.predictionsData,
    predictionsError: attendancePredictions.predictionsError,
    predictionsIsLoading: attendancePredictions.predictionsIsLoading,
    refetchPredictions: attendancePredictions.refetchPredictions,

    // Patterns
    patternsData: attendancePatterns.patternsData,
    patternsError: attendancePatterns.patternsError,
    patternsIsLoading: attendancePatterns.patternsIsLoading,
    refetchPatterns: attendancePatterns.refetchPatterns,

    // Summary
    summaryData: analyticsSummary.summaryData,
    summaryError: analyticsSummary.summaryError,
    summaryIsLoading: analyticsSummary.summaryIsLoading,
    refetchSummary: analyticsSummary.refetchSummary,

    // Turnover Predictions
    turnoverData: turnoverPredictions.turnoverData,
    turnoverError: turnoverPredictions.turnoverError,
    turnoverIsLoading: turnoverPredictions.turnoverIsLoading,
    refetchTurnover: turnoverPredictions.refetchTurnover,

    // Turnover Summary
    turnoverSummaryData: turnoverSummary.turnoverSummaryData,
    turnoverSummaryError: turnoverSummary.turnoverSummaryError,
    turnoverSummaryIsLoading: turnoverSummary.turnoverSummaryIsLoading,
    refetchTurnoverSummary: turnoverSummary.refetchTurnoverSummary,

    // Combined loading state - more granular
    isLoading: analyticsSummary.summaryIsLoading,
    isRiskLoading: riskAssessment.riskIsLoading,
    isPredictionsLoading: attendancePredictions.predictionsIsLoading,
    isPatternsLoading: attendancePatterns.patternsIsLoading,
    isTurnoverSummaryLoading: turnoverSummary.turnoverSummaryIsLoading,
    isTurnoverPredictionsLoading: turnoverPredictions.turnoverIsLoading,

    // Combined error state
    hasError: riskAssessment.riskError ||
              attendancePredictions.predictionsError ||
              attendancePatterns.patternsError ||
              analyticsSummary.summaryError ||
              turnoverPredictions.turnoverError ||
              turnoverSummary.turnoverSummaryError,

    // Refetch all data
    refetchAll: () => {
      analyticsSummary.refetchSummary();
      riskAssessment.refetchRisk();
      attendancePredictions.refetchPredictions();
      attendancePatterns.refetchPatterns();
      turnoverPredictions.refetchTurnover();
      turnoverSummary.refetchTurnoverSummary();
    },
  };
}; 


 